import React, { memo, useContext } from 'react';
import dynamic from 'next/dynamic';
import { SearchModes } from '@/domainLogic/index.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { CardContainer, CardOuterContainer } from './styled';
import Details from './Details';
import Gallery from './Gallery';
import { CardsContext } from '../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { airportMarkerSelection, markerSelection } from '../SearchPageContainer/MapContainer/utils';
import { type CardProps, type HandleDealCardCtaCallBackProps } from './types';

const CardBorder = dynamic(() => import('./CardBorder'), { ssr: false });
const DebugInfo = dynamic(() => import('./Debug'), { ssr: false });

const EvolutionCard = memo(
  ({ dealCard, isOverlay, onClose, setCardOverHotelId, dealCardCtaCallBack, type, className }: CardProps) => {
    const { searchInfoData, curiosityParams } = usePageWrapperData();
    const { listView } = useContext(SearchPageContext);
    const { isMobile } = useCommonClient();
    const { isWaiting, isLoading } = useContext(CardsContext);
    const searchMode = searchInfoData?.searchMode;
    const promotionTypes = dealCard?.promotionTypes || [];
    const promotionTheme = dealCard?.promotionTheme || null;
    const isSoldOut = dealCard?.product?.dates?.soldOut;
    const isNotClickable = isSoldOut || isWaiting || isLoading || !dealCard;
    const isPreselected = !isOverlay && dealCard?.isHighlighted;
    const isHO = searchMode === SearchModes.HO;
    const debugEnabled = curiosityParams?.debugEnabled;
    const hotelId = dealCard?.product?.accommodation?.hotelId;
    const { highlightedHotelId } = useSearchDealsPageData();
    const isHighlighted = highlightedHotelId === hotelId && !listView && !isOverlay;

    const handleDealCardCtaCallBack = ({
      e,
      payload,
      selectedAlternativeDatesIndex,
    }: HandleDealCardCtaCallBackProps) => {
      e?.preventDefault();
      if (!isWaiting && dealCardCtaCallBack) {
        dealCardCtaCallBack({
          e,
          dealCard,
          payload,
          ...(typeof selectedAlternativeDatesIndex === 'number' ? { selectedAlternativeDatesIndex } : {}),
        });
      }
    };
    const handleOnMouseEnter = (): void => {
      if (!isMobile) {
        if (setCardOverHotelId && dealCard?.product?.rate?.flags?.tsh) {
          setCardOverHotelId(dealCard?.product?.accommodation?.hotelId);
        }
        if (!isOverlay) {
          markerSelection(hotelId, true);
          if (dealCard?.product?.geo?.destinationAp) airportMarkerSelection(dealCard.product.geo.destinationAp, true);
        }
      }
    };

    const handleOnMouseLeave = (): void => {
      if (!isMobile) {
        if (setCardOverHotelId && dealCard?.product?.rate?.flags?.tsh) {
          setCardOverHotelId(undefined);
        }
        if (!isOverlay) {
          markerSelection(hotelId, false);
          if (dealCard?.product?.geo?.destinationAp) airportMarkerSelection(dealCard.product.geo.destinationAp, false);
        }
      }
    };

    return (
      <CardOuterContainer
        role={isOverlay ? 'dialog' : undefined}
        aria-labelledby={isOverlay ? `card-title-${dealCard?.product?.accommodation?.hotelId}` : undefined}
        isHighlighted={isHighlighted}
      >
        {isPreselected && <CardBorder isLoading={isWaiting || isLoading} />}
        <CardContainer
          isOverlay={isOverlay}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          isNotClickable={isNotClickable}
          data-testid="card-container"
          isHO={isHO}
          className={className}
          type={type}
        >
          <Gallery
            dealCard={dealCard}
            isOverlay={isOverlay}
            isNotClickable={isNotClickable}
            promotionTypes={promotionTypes}
            handleDealCardCtaCallBack={handleDealCardCtaCallBack}
            onClose={onClose}
          />
          <Details
            dealCard={dealCard}
            isOverlay={isOverlay}
            isNotClickable={isNotClickable}
            promotionType={promotionTheme}
            promotionTypes={promotionTypes}
            type={type}
            handleDealCardCtaCallBack={handleDealCardCtaCallBack}
          />
        </CardContainer>
        {debugEnabled && !isOverlay && dealCard && (
          <ErrorBoundary id="Card/Debug">
            <DebugInfo dealCard={dealCard} />
          </ErrorBoundary>
        )}
      </CardOuterContainer>
    );
  },
  (prevProps: CardProps, nextProps: CardProps) => {
    if (JSON.stringify(prevProps.dealCard) !== JSON.stringify(nextProps.dealCard)) return false;
    if (prevProps.isOverlay !== nextProps.isOverlay) return false;
    if (prevProps.className !== nextProps.className) return false;
    return true;
  }
);

EvolutionCard.displayName = 'Card';

export default EvolutionCard;
